import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import * as styles from './roiCalculator.module.css';
import { AlmaLink } from '../alma-link';
import { PortableText } from '../portableText';

export function RoiCalculator({
  benefits,
  benefitsTitle,
  disclaimerCopy,
  resultsCTA,
  resultsNoSavingsBenefits,
  resultsNoSavingsBenefitsTitle,
  resultsNoSavingsCTA,
  resultsNoSavingsSubtitle,
  resultsNoSavingsTitle,
  resultsTitle,
  roiCalculatorGroups,
  title
}) {
  // state
  const [timeCalculationValues, setTimeCalculationValues] = useState([]);
  const [costCalculationValues, setCostCalculationValues] = useState([]);
  const [unitCostCalculationValues, setUnitCostCalculationValues] = useState([]);
  const [monthlyYearlyOptions, setMonthlyYearlyOptions] = useState([]);
  const [timeSavingsAnnual, setTimeSavingsAnnual] = useState(0);
  const [costSavingsAnnual, setCostSavingsAnnual] = useState(0);
  const [totalSavings, setTotalSavings] = useState('');
  const [showNoSavingsResults, setShowNoSavingsResults] = useState(false);
  const [useYearlyResults, setUseYearlyResults] = useState(true);
  const [needsRecalculation, setNeedsRecalculation] = useState(false);

  // set all initial time calculation values to be the default time value
  useEffect(() => {
    const initialTimeCalculationValues = roiCalculatorGroups.map(calculatorGroup => {
      const { calculatorInputs } = calculatorGroup;

      return calculatorInputs.map(calculatorInput => {
        if (calculatorInput?.showTimeCalculation) {
          return {
            [`${calculatorInput?._key}--timeCalculation`]: calculatorInput?.defaultTime
          };
        }
      });
    }).flat().filter(data => data !== undefined);

    setTimeCalculationValues(initialTimeCalculationValues);
  }, []);

  // set all initial cost calculation values to be the default cost value
  useEffect(() => {
    const initialCostCalculationValues = roiCalculatorGroups.map(calculatorGroup => {
      const { calculatorInputs } = calculatorGroup;

      return calculatorInputs.map(calculatorInput => {
        if (calculatorInput?.showCostCalculation) {
          return {
            [`${calculatorInput?._key}--costCalculation`]: calculatorInput?.defaultCost
          };
        }
      });
    }).flat().filter(data => data !== undefined);

    setCostCalculationValues(initialCostCalculationValues);
  }, []);

  // set all initial monthly yearly options to monthly by default
  useEffect(() => {
    const initialMonthlyYearlyOptions = roiCalculatorGroups.map(calculatorGroup => {
      const { calculatorInputs } = calculatorGroup;

      return calculatorInputs.map(calculatorInput => {
        if (calculatorInput?.showMonthYearOption) {
          return {
            key: calculatorInput?._key,
            value: 'monthOption'
          };
        }
      });
    }).flat().filter(data => data !== undefined);

    setMonthlyYearlyOptions(initialMonthlyYearlyOptions);
  }, []);

  // set all initial unit cost calculation values to be the default unit cost value
  useEffect(() => {
    const initialUnitCostCalculationValues = roiCalculatorGroups.map(calculatorGroup => {
      const { calculatorInputs } = calculatorGroup;

      return calculatorInputs.map(calculatorInput => {
        if (calculatorInput?.showUnitCostCalculation) {
          return {
            [`${calculatorInput?._key}--unitCostCalculation`]: calculatorInput?.defaultUnitAmount
          };
        }
      });
    }).flat().filter(data => data !== undefined);

    setUnitCostCalculationValues(initialUnitCostCalculationValues);
  }, []);

  // perform initial calculation
  useEffect(() => {
    calculateResults();
  }, [timeCalculationValues, costCalculationValues, unitCostCalculationValues]);

  // update total savings based on results
  useEffect(() => {
    console.log('Time Savings: ', timeSavingsAnnual);
    console.log('Cost Savings: ', costSavingsAnnual);

    const timeSavings = useYearlyResults ? timeSavingsAnnual : Math.round(timeSavingsAnnual / 12);
    const costSavings = useYearlyResults ? costSavingsAnnual : Math.round(costSavingsAnnual / 12);

    if (timeSavingsAnnual > 0 && costSavingsAnnual > 0) {
      const savings = `${timeSavings} hours + $${costSavings}`;
      setTotalSavings(savings);
      setShowNoSavingsResults(false);
    } else if (timeSavingsAnnual > 0 && costSavingsAnnual <= 0) {
      const savings = `${timeSavings} hours`;
      setTotalSavings(savings);
      setShowNoSavingsResults(false);
    } else if (timeSavingsAnnual <= 0 && costSavingsAnnual > 0) {
      const savings = `$${costSavings}`;
      setTotalSavings(savings);
      setShowNoSavingsResults(false);
    } else {
      setShowNoSavingsResults(true);
    }
  }, [timeSavingsAnnual, costSavingsAnnual, useYearlyResults]);

  const getTimeCalculationValue = (id) => {
    // find the value in timeCalculationValues where they key matches id
    const rawValue = timeCalculationValues.map(timeValue => {
      return timeValue[id];
    }).filter(data => data !== undefined);

    const value = rawValue[0];

    if (value !== undefined) {
      return value;
    }
  };

  const handleTimeCalculationValueChange = (event) => {
    const value = event.target.value;
    const id = event.target.id;

    // if value is not a number, return
    if (isNaN(value)) {
      return;
    }

    // find where key of timeCalculationValues === id
    const timeCalculationValue = timeCalculationValues.map(timeValue => {
      if (timeValue[id] !== undefined) {
        return {
          [id]: value
        };
      }

      return timeValue;
    });

    setTimeCalculationValues(timeCalculationValue);
    setNeedsRecalculation(true);
  };

  const getCostCalculationValue = (id) => {
    // find the value in costCalculationValues where they key matches id
    const rawValue = costCalculationValues.map(costValue => {
      return costValue[id];
    }).filter(data => data !== undefined);

    const value = rawValue[0];

    if (value !== undefined) {
      return value;
    }
  };

  const handleCostCalculationValueChange = (event) => {
    const value = event.target.value;
    const id = event.target.id;

    // if value is not a number, return
    if (isNaN(value)) {
      return;
    }

    // find where key of costCalculationValues === id
    const costCalculationValue = costCalculationValues.map(costValue => {
      if (costValue[id] !== undefined) {
        return {
          [id]: value
        };
      }

      return costValue;
    });

    setCostCalculationValues(costCalculationValue);
    setNeedsRecalculation(true);
  };

  const getCostCalculationMonthYearValue = (key, value) => {
    // find the value in monthlyYearlyOptions where they key matches id
    const rawValue = monthlyYearlyOptions.map(monthYearOption => {
      if (monthYearOption.key === key) {
        return monthYearOption.value;
      }
    }).filter(data => data !== undefined);

    const selectedValue = rawValue[0];

    if (selectedValue === value) {
      return true;
    } else {
      return false;
    }
  };

  const handleCostCalculationMonthYearChange = (key, value) => {
    const existingIndex = monthlyYearlyOptions.findIndex(item => item.key === key);

    if (existingIndex !== -1) {
      // if key already exists, update the value
      const updatedArray = [...monthlyYearlyOptions];
      updatedArray[existingIndex] = { ...updatedArray[existingIndex], value };
      setMonthlyYearlyOptions(updatedArray);
    } else {
      // if key doesn't exist, add a new key-value pair
      setMonthlyYearlyOptions(prevArray => [...prevArray, { key, value }]);
    }

    setNeedsRecalculation(true);
  };


  const getUnitCostCalculationValue = (id) => {
    // find the value in unitCostCalculationValues where they key matches id
    const rawValue = unitCostCalculationValues.map(unitCostValue => {
      return unitCostValue[id];
    }).filter(data => data !== undefined);

    const value = rawValue[0];

    if (value !== undefined) {
      return value;
    }
  };

  const handleUnitCostCalculationValueChange = (event) => {
    const value = event.target.value;
    const id = event.target.id;

    // if value is not a number, return
    if (isNaN(value)) {
      return;
    }

    // find where key of unitCostCalculationValues === id
    const unitCostCalculationValue = unitCostCalculationValues.map(unitCostValue => {
      if (unitCostValue[id] !== undefined) {
        return {
          [id]: value
        };
      }

      return unitCostValue;
    });

    setUnitCostCalculationValues(unitCostCalculationValue);
    setNeedsRecalculation(true);
  };

  const calculateResults = () => {
    console.log('Calculating...');

    // map over all inputs and create an array of objects with the values to calculate
    const valuesToCalculate = roiCalculatorGroups.map(calculatorGroup => {
      const { calculatorInputs } = calculatorGroup;

      return calculatorInputs.map((calculatorInput) => {
        // get the time calculation value and default alma time
        const timeCalculationValue = getTimeCalculationValue(`${calculatorInput?._key}--timeCalculation`);
        const defaultAlmaTime = calculatorInput?.defaultAlmaTime;

        // get the cost calculation value and default alma cost
        const costCalculationValue = getCostCalculationValue(`${calculatorInput?._key}--costCalculation`);
        const defaultAlmaCost = calculatorInput?.defaultAlmaCost;
        const monthlyYearlyOption = monthlyYearlyOptions.find(option => option.key === calculatorInput?._key);

        // get the unit cost calculation value and default unit cost
        const unitCostCalculationValues = getUnitCostCalculationValue(`${calculatorInput?._key}--unitCostCalculation`);
        const defaultUnitCost = calculatorInput?.defaultUnitCost;
        const defaultUnitAmount = calculatorInput?.defaultUnitAmount;
        const defaultAlmaUnitCost = calculatorInput?.defaultAlmaUnitCost;

        // only return defined values
        return ({
          ...(timeCalculationValue && { timeCalculationValue: Number(timeCalculationValue) }),
          ...(defaultAlmaTime && { defaultAlmaTime }),
          ...(costCalculationValue && { costCalculationValue: Number(costCalculationValue) }),
          ...(defaultAlmaCost && { defaultAlmaCost }),
          ...(monthlyYearlyOption && { monthlyYearlyOption: monthlyYearlyOption.value }),
          ...(unitCostCalculationValues && { unitCostCalculationValues: Number(unitCostCalculationValues) }),
          ...(defaultUnitCost && { defaultUnitCost }),
          ...(defaultUnitAmount && { defaultUnitAmount }),
          ...(defaultAlmaUnitCost && { defaultAlmaUnitCost })
        });
      }
      );
    }).flat();

    // add up all the time calculation values
    const totalTimeCalculation = valuesToCalculate.reduce((accumulator, currentValue) => {
      if (currentValue.timeCalculationValue === undefined) {
        return accumulator;
      }
      return accumulator + currentValue.timeCalculationValue;
    }, 0);

    const totalTimeToSubtract = valuesToCalculate.reduce((accumulator, currentValue) => {
      if (currentValue.defaultAlmaTime === undefined) {
        return accumulator;
      }
      return accumulator + currentValue?.defaultAlmaTime;
    }, 0);

    const roundedTotalTimeToSubtract = Math.round(totalTimeToSubtract * 10) / 10;
    const totalTimeSavingsAnnual = Math.round((totalTimeCalculation - roundedTotalTimeToSubtract) * 52);

    setTimeSavingsAnnual(totalTimeSavingsAnnual);

    // add up all the cost calculation values
    const totalCostCalculation = valuesToCalculate.reduce((accumulator, currentValue) => {
      if (currentValue.costCalculationValue === undefined) {
        return accumulator;
      }

      if (currentValue.monthlyYearlyOption === 'yearOption') {
        return accumulator + (currentValue.costCalculationValue / 12);
      }

      return accumulator + currentValue.costCalculationValue;
    }, 0);

    const totalCostToSubtract = valuesToCalculate.reduce((accumulator, currentValue) => {
      if (currentValue.defaultAlmaCost === undefined) {
        return accumulator;
      }
      return accumulator + currentValue?.defaultAlmaCost;
    }, 0);

    // add up all the unit cost calculation values
    const totalUnitCostCalculation = valuesToCalculate.reduce((accumulator, currentValue) => {
      if (
        (currentValue.unitCostCalculationValues === undefined) || (currentValue.defaultUnitCost === undefined)
      ) {
        return accumulator;
      }
      return accumulator + (currentValue.unitCostCalculationValues * currentValue.defaultUnitCost);
    }, 0);

    const totalAlmaUnitCostCalculation = valuesToCalculate.reduce((accumulator, currentValue) => {
      if (
        (currentValue.unitCostCalculationValues === undefined) || (currentValue.defaultAlmaUnitCost === undefined)
      ) {
        return accumulator;
      }
      return accumulator + (currentValue.unitCostCalculationValues * currentValue.defaultAlmaUnitCost);
    }, 0);

    // calculate total cost savings
    const costDifferent = totalCostCalculation - totalCostToSubtract;
    const totalCostSavingsAnnual = costDifferent * 12;

    // calculate unit cost savings
    const unitCostSavingsAnnual = totalUnitCostCalculation * 12;
    const almaUnitCostSavingsAnnual = totalAlmaUnitCostCalculation * 12;
    const totalUnitCostSavingsAnnual = unitCostSavingsAnnual - almaUnitCostSavingsAnnual;

    // add up all the cost calculation values
    const totalCostAndUnitCostSavingsValue = totalCostSavingsAnnual + totalUnitCostSavingsAnnual;
    setCostSavingsAnnual(totalCostAndUnitCostSavingsValue);

    // assign url params to populate hidden fields in the marketo form
    const urlParams = new URLSearchParams(window.location.search);

    urlParams.set('calcTimeSavings', totalTimeSavingsAnnual);
    urlParams.set('calcCostSavings', totalCostAndUnitCostSavingsValue);

    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
  };

  const recalculateResults = () => {
    calculateResults();
    setNeedsRecalculation(false);
  };


  return (
    <div
      className={
        classNames(
          'responsive-layout',
          styles.moduleContainer
        )
      }
    >
      <div className={styles.container}>
        <div className={styles.calculatorContainer}>
          <div className={styles.headerContainer}>
            {title && (
              <h2 className='body-display'>{title}</h2>
            )}
          </div>
          {roiCalculatorGroups && (
            <div className={styles.calculatorGroupsContainer}>
              {roiCalculatorGroups?.map(calculatorGroup => {
                const { _key, calculatorInputs } = calculatorGroup;

                return (
                  <div key={_key} className={styles.calculatorInputsGroup}>
                    <div className={styles.groupHeading}>
                      <h3 className='body-xl'>
                        <strong>{calculatorGroup.calculatorGroupTitle}</strong>
                      </h3>
                    </div>
                    <div className={styles.calculatorInputGroup}>
                      {calculatorInputs?.map((calculatorInput, index) => {
                        return (
                          <div key={index} className={styles.calculatorInput}>
                            <div className={styles.inputCopyGroup}>
                              <h3 className='body'>
                                {calculatorInput.roiCalculatorInputTitle}
                              </h3>
                              <p
                                className={
                                  classNames(
                                    'body-s',
                                    styles.inputSubtitle
                                  )
                                }
                              >
                                {calculatorInput.roiCalculatorInputSubtitle}
                              </p>
                            </div>
                            <div className={styles.inputCalculationContainer}>
                              {calculatorInput?.showTimeCalculation && (
                                <div className={styles.inputToolOptions}>
                                  <label className={styles.inputToolInputContainer}>
                                    <input
                                      id={`${calculatorInput?._key}--timeCalculation`}
                                      className={styles.inputToolInput}
                                      value={
                                        getTimeCalculationValue(`${calculatorInput?._key}--timeCalculation`) ?? 0
                                      }
                                      onChange={
                                        (event) => handleTimeCalculationValueChange(event)
                                      }
                                    />
                                    <p className={styles.inputToolInputLabel}>
                                      hrs/week
                                    </p>
                                  </label>
                                </div>
                              )}
                              {calculatorInput?.showCostCalculation && (
                                <div className={styles.inputToolOptions}>
                                  <label className={styles.inputToolInputContainer}>
                                    <input
                                      id={`${calculatorInput?._key}--costCalculation`}
                                      className={styles.inputToolInputCost}
                                      value={
                                        getCostCalculationValue(`${calculatorInput?._key}--costCalculation`) ?? 0
                                      }
                                      onChange={
                                        (event) => handleCostCalculationValueChange(event)
                                      }
                                    />
                                    <p className={styles.inputCurrency}>$</p>
                                    {calculatorInput?.showMonthYearOption ? (
                                      // add a div with two radio inputs, one that has a label per month, and one that has a label per year
                                      <div className={styles.inputLabelContainer}>
                                        <label>
                                          <input
                                            type='radio'
                                            name={`${calculatorInput?._key}--monthOption`}
                                            checked={getCostCalculationMonthYearValue(calculatorInput?._key, 'monthOption')}
                                            onChange={
                                              () => handleCostCalculationMonthYearChange(calculatorInput?._key, 'monthOption')
                                            }
                                          />
                                          <p
                                            className={
                                              classNames(
                                                'body',
                                                styles.inputToolInputRadioLabel
                                              )
                                            }
                                          >
                                            per month
                                          </p>
                                        </label>
                                        <label>
                                          <input
                                            type='radio'
                                            name={`${calculatorInput?._key}--yearOption`}
                                            checked={getCostCalculationMonthYearValue(calculatorInput?._key, 'yearOption')}
                                            onChange={
                                              () => handleCostCalculationMonthYearChange(calculatorInput?._key, 'yearOption')
                                            }
                                          />
                                          <p
                                            className={
                                              classNames(
                                                'body',
                                                styles.inputToolInputRadioLabel
                                              )
                                            }
                                          >
                                            per year
                                          </p>
                                        </label>
                                      </div>
                                    ) : (
                                      <p className={styles.inputToolInputLabel}>
                                        per month
                                      </p>
                                    )}
                                  </label>
                                </div>
                              )}
                              {calculatorInput?.showUnitCostCalculation && (
                                <div className={styles.inputToolOptions}>
                                  <label className={styles.inputToolInputContainer}>
                                    <input
                                      id={`${calculatorInput?._key}--unitCostCalculation`}
                                      className={styles.inputToolInput}
                                      value={
                                        getUnitCostCalculationValue(`${calculatorInput?._key}--unitCostCalculation`) ?? 0
                                      }
                                      onChange={
                                        (event) => handleUnitCostCalculationValueChange(event)
                                      }
                                    />
                                    {calculatorInput?.defaultUnitLabel && (
                                      <p className={styles.inputToolInputLabel}>
                                        {calculatorInput?.defaultUnitLabel}
                                      </p>
                                    )}
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {!showNoSavingsResults && (
          <div className={styles.resultsContainer}>
            {resultsTitle && (
              <h2 className='body-display'>{resultsTitle}</h2>
            )}
            <div>
              <div className={styles.results}>
                <div className={styles.resultsHeader}>
                  <h2 className='heading-l'>{totalSavings}</h2>
                  <div className={styles.resultsTimeContainer}>
                    <p
                      className={
                        useYearlyResults ?
                          styles.resultsToggleLabel : styles.resultsToggleLabelActive
                      }
                    >per month</p>
                    <label className={styles.resultsToggle}>
                      <input
                        type='checkbox'
                        checked={useYearlyResults}
                        onChange={() => setUseYearlyResults(!useYearlyResults)}
                      />
                      <span className={styles.slider}></span>
                    </label>
                    <p
                      className={
                        useYearlyResults ?
                          styles.resultsToggleLabelActive : styles.resultsToggleLabel
                      }
                    >per year</p>
                  </div>
                </div>
                <div className={styles.divider} />
                <div className={styles.benefitsContainer}>
                  <div className={styles.benefitsTextContainer}>
                    {benefitsTitle && (
                      <h3 className='body-l'><strong>{benefitsTitle}</strong></h3>
                    )}
                    <PortableText
                      blocks={benefits}
                      className={styles.benefits}
                    />
                  </div>
                  {resultsCTA?.title && (
                    <div className={styles.ctaContainer}>
                      <AlmaLink cta={resultsCTA} />
                    </div>
                  )}
                </div>
                <div
                  className={
                    classNames(
                      styles.resultsOverlay,
                      needsRecalculation && styles.resultsOverlayVisible

                    )
                  }
                >
                  <button
                    className={styles.recalculateButton}
                    onClick={recalculateResults}
                  >
                    Calculate new results
                  </button>
                </div>
              </div>
              {disclaimerCopy && (
                <p
                  className={
                    classNames(
                      'body-xs',
                      styles.footnoteText
                    )
                  }
                >
                  {disclaimerCopy}
                </p>
              )}
            </div>
          </div>
        )}
        {showNoSavingsResults && (
          <div className={styles.resultsContainer}>
            <div className={styles.results}>
              <div className={styles.resultsNoSavingsHeader}>
                <h2 className='body-display-sm'>
                  {resultsNoSavingsTitle}
                </h2>
                <PortableText
                  blocks={resultsNoSavingsSubtitle}
                  className='body-l'
                />
              </div>
              <div className={styles.divider} />
              <div className={styles.benefitsContainer}>
                <div className={styles.benefitsTextContainer}>
                  {resultsNoSavingsBenefitsTitle && (
                    <h3 className='body-l'>
                      <strong>{resultsNoSavingsBenefitsTitle}</strong>
                    </h3>
                  )}
                  <PortableText
                    blocks={resultsNoSavingsBenefits}
                    className={styles.benefits}
                  />
                </div>
                {resultsNoSavingsCTA?.title && (
                  <div className={styles.ctaContainer}>
                    <AlmaLink cta={resultsNoSavingsCTA} />
                  </div>
                )}
              </div>
              {needsRecalculation && (
                <div className={styles.resultsOverlay}>
                  <button
                    className={styles.recalculateButton}
                    onClick={recalculateResults}
                  >
                    Calculate new results
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
